// src/components/MainLayout.js

import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  SmartToy as BotIcon,
  Description as LogsIcon,
  AccountBalance as BillingIcon,
  Support as SupportIcon,
  MenuBook as KnowledgebaseIcon,
  ConfirmationNumber as TicketsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  OpenInNew as ExternalIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { text: 'My Bots', path: '/bots', icon: <BotIcon /> },
  { text: 'Logs', path: '/logs', icon: <LogsIcon /> },
  { text: 'Support', path: '/support', icon: <SupportIcon /> },
  { divider: true },
  { text: 'Billing', href: 'https://portal.highland-networks.com', icon: <BillingIcon />, external: true },
  { text: 'Knowledgebase', href: 'https://portal.highland-networks.com/knowledgebase', icon: <KnowledgebaseIcon />, external: true },
  { divider: true },
  { text: 'Account', path: '/account', icon: <AccountCircleIcon /> },
];

const adminItems = [
  { text: 'All Bots', path: '/admin/bots', icon: <BotIcon /> },
  { text: 'Tickets', path: '/admin/tickets', icon: <TicketsIcon /> },
  { text: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
];

function MainLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #5c6bc0, #42a5f5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.85rem',
                color: '#fff',
              }}
            >
              HN
            </Box>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Highland Networks
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar
                alt={user.username}
                src={user.avatar}
                sx={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.1)' }}
              />
              <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
                {user.username}
              </Typography>
              {user.role === 'admin' && (
                <Chip label="Admin" size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
              )}
              <Button
                size="small"
                startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                onClick={handleLogout}
                sx={{ color: 'text.secondary', ml: 1, fontSize: '0.8rem' }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 1, display: 'flex', flexDirection: 'column', flex: 1 }}>
          <List sx={{ px: 1.5 }}>
            {menuItems.map((item, index) => {
              if (item.divider) {
                return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
              }

              const active = !item.external && isActive(item.path);

              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() =>
                      item.external
                        ? window.open(item.href, '_blank', 'noopener')
                        : navigate(item.path)
                    }
                    sx={{
                      borderRadius: '8px',
                      py: 1,
                      px: 1.5,
                      bgcolor: active ? 'rgba(92, 107, 192, 0.12)' : 'transparent',
                      color: active ? 'primary.light' : 'text.secondary',
                      '&:hover': {
                        bgcolor: active ? 'rgba(92, 107, 192, 0.18)' : 'rgba(255,255,255,0.04)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? 'primary.light' : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: active ? 600 : 400,
                      }}
                    />
                    {item.external && (
                      <ExternalIcon sx={{ fontSize: 14, opacity: 0.4 }} />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          {user && user.role === 'admin' && (
            <>
              <Divider sx={{ mx: 1.5 }} />
              <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    color: 'text.secondary',
                  }}
                >
                  Administration
                </Typography>
              </Box>
              <List sx={{ px: 1.5, pb: 2 }}>
                {adminItems.map((item) => {
                  const active = isActive(item.path);

                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        sx={{
                          borderRadius: '8px',
                          py: 1,
                          px: 1.5,
                          bgcolor: active ? 'rgba(92, 107, 192, 0.12)' : 'transparent',
                          color: active ? 'primary.light' : 'text.secondary',
                          '&:hover': {
                            bgcolor: active ? 'rgba(92, 107, 192, 0.18)' : 'rgba(255,255,255,0.04)',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: active ? 'primary.light' : 'text.secondary',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default MainLayout;
